import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { Align } from 'design/types';
import { Icon } from '../Icon';
import { LoaderDots } from '../Loader';
import { IButtonProps } from './ButtonProps';
import ButtonStyles from './ButtonStyles';

const Layout = styled.button`
  ${ButtonStyles}
`;

const StyledLink = styled(({ isLoading, isProcessing, ...rest }) => <Link {...rest} />)<Partial<IButtonProps>>`
  ${ButtonStyles};
  text-decoration: none;

  ${({ appearance }) => {
    let clr = '';

    switch (appearance) {
      case 'primary':
      case 'tertiary':
        clr = theme.color.white;
        break;
      case 'secondary':
        // eslint-disable-next-line prefer-destructuring
        clr = theme.color.neutral[900];
        break;
      default:
        break;
    }

    return clr ? `&:active { color: ${clr}; }` : '';
  }}
`;

const Label = styled.span<Partial<IButtonProps>>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: inherit;
  opacity: ${({ isLoading, isProcessing }) => (isProcessing || isLoading ? 0 : 1)};

  .icon {
    ${({ iconPosition }) => `${iconPosition === Align.LEFT ? 'margin-right' : 'margin-left'}: ${toRem(4)}`};

    ${({ appearance }) => {
      let clr: string;

      switch (appearance) {
        case 'primary':
        case 'tertiary':
          clr = theme.color.white;
          break;
        case 'secondary':
          // eslint-disable-next-line prefer-destructuring
          clr = theme.color.neutral[900];
          break;
        default:
          break;
      }

      return clr ? `fill: ${clr};` : '';
    }}
  }
`;

const NonDefaultStateContainer = styled.span.attrs({ id: 'container' })<Partial<IButtonProps>>`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: ${toRem(theme.button.height.large / 2)};
  overflow: hidden;

  ${({ size }) => size === 'small' && `border-radius: ${toRem(theme.button.height.small / 2)};`}

  > span {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

const StyledSkeleton = styled(Skeleton).attrs({ id: 'skeley' })`
  && {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 0;
  }
`;

const TooltipWrapper = styled.div`
  width: fit-content;
  height: fit-content;
`;

const Button = ({
  appearance = 'primary',
  className,
  disabled,
  label,
  isLoading,
  isProcessing,
  onClick,
  ref,
  size = 'large',
  to,
  type = 'button',
  icon,
  iconPosition = Align.LEFT,
  tooltipId,
  'data-cy': dataCy = 'button',
  ...other
}: IButtonProps) => {
  const disableClick = disabled || isLoading || isProcessing;
  const handleOnClick = () => {
    if (disableClick) {
      return null;
    }
    if (onClick) {
      return onClick();
    }
    return null;
  };

  const buttonContent = (
    <>
      <Label isLoading={isLoading} isProcessing={isProcessing} appearance={appearance} iconPosition={iconPosition}>
        {icon && iconPosition === Align.LEFT && <Icon className="icon" icon={icon} />}
        {label}
        {icon && iconPosition === Align.RIGHT && <Icon className="icon" icon={icon} />}
      </Label>

      {isProcessing && (
        <NonDefaultStateContainer size={size} data-cy="processing-container">
          <LoaderDots size={size === 'large' ? 'large' : 'small'} color={appearance === 'primary' || appearance === 'destructive' ? theme.color.white : theme.color.neutral[900]} />
        </NonDefaultStateContainer>
      )}

      {isLoading && (
        <NonDefaultStateContainer size={size} data-cy="loading-container">
          <StyledSkeleton />
        </NonDefaultStateContainer>
      )}
    </>
  );

  let button: JSX.Element;

  if (to && !disabled) {
    button = (
      <StyledLink {...other} appearance={appearance} isLoading={isLoading} isProcessing={isProcessing} size={size} disabled={disabled} to={to} onClick={handleOnClick} className={className} data-cy={dataCy}>
        {buttonContent}
      </StyledLink>
    );
  } else {
    button = (
      <Layout {...other} type={type} appearance={appearance} isLoading={isLoading} isProcessing={isProcessing} size={size} disabled={disabled} onClick={handleOnClick} className={className} ref={ref} data-cy={dataCy}>
        {buttonContent}
      </Layout>
    );
  }

  return tooltipId ? <TooltipWrapper data-tooltip-id={tooltipId}>{button}</TooltipWrapper> : button;
};

export default Button;
